import { API_PREFIX, API_ROUTES, api, routePath, } from "@mfa/v5-contracts";
import { createORPCClient } from "@orpc/client";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import { assertCredentialAllowed, credentialHeaders, credentialsMode, } from "./credentials.js";
import { ApiClientError, isAbortError, isRedirect, networkError, readError, redirectRefused, toApiClientError, } from "./errors.js";
import { streamRunEvents } from "./stream.js";
import { API_VERSION_HEADER, CLIENT_HEADER, CLIENT_VERSION } from "./version.js";
/**
 * A uuid for `x-request-id`, a commandId or an Idempotency-Key: crypto.randomUUID where the
 * runtime has it (a page served over plain http has none), else random bytes.
 */
export function randomId() {
    const crypto = globalThis.crypto;
    if (typeof crypto?.randomUUID === "function")
        return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
/** The site with no trailing slash and no `/api/v5`. */
export function siteBase(baseUrl) {
    const trimmed = baseUrl.replace(/\/+$/, "");
    return trimmed.endsWith(API_PREFIX) ? trimmed.slice(0, -API_PREFIX.length) : trimmed;
}
/** An absolute URL: a relative base is resolved against the page's origin. */
function absolute(url) {
    if (/^[a-z][a-z0-9+.-]*:/i.test(url))
        return url;
    const origin = globalThis.location?.origin;
    if (!origin || origin === "null")
        throw new TypeError(`a relative baseUrl needs a page origin to resolve against: ${url}`);
    return new URL(url, origin).href;
}
function createCore(options, credential) {
    const base = siteBase(options.baseUrl);
    const transport = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    const requestId = options.requestId ?? randomId;
    let lastVersion;
    const apiUrl = (path) => `${base}${API_PREFIX}${path}`;
    async function send(url, init, { authenticate = true } = {}) {
        const headers = new Headers(init.headers);
        if (authenticate)
            for (const [name, value] of Object.entries(await credentialHeaders(credential)))
                headers.set(name, value);
        headers.set(CLIENT_HEADER, CLIENT_VERSION);
        if (!headers.has("x-request-id"))
            headers.set("x-request-id", requestId());
        let response;
        try {
            response = await transport(absolute(url), {
                ...init,
                headers,
                credentials: authenticate ? credentialsMode(credential) : "omit",
                // Never followed: fetch keeps a header such as X-Api-Key across a redirect to another
                // origin. Every call, procedure, route or stream, is answered here instead.
                redirect: "manual",
            });
        }
        catch (error) {
            if (isAbortError(error) || init.signal?.aborted)
                throw error;
            throw networkError(url, error);
        }
        const version = response.headers.get(API_VERSION_HEADER);
        if (version)
            lastVersion = version;
        if (isRedirect(response)) {
            await response.body?.cancel().catch(() => { });
            throw redirectRefused(url, response);
        }
        return response;
    }
    // The contract decides each procedure's path and method (POST /<area>/<name>, none declares a
    // route of its own) and the body is the input itself, so a procedure call is exactly what a
    // hand-written `fetch` of docs/HEADLESS.md section 6 would send.
    const link = new OpenAPILink(api, {
        url: () => absolute(apiUrl("")),
        headers: ({ context }) => context?.idempotencyKey ? { "idempotency-key": context.idempotencyKey } : {},
        // Sent as (url, init), so the call goes out exactly as the hand-written ones do. A refusal is
        // read here, at its status and with its headers, from whichever body it has: the one body of
        // H.16, or a key or bearer refusal from before the procedure (errors.ts reads them all).
        fetch: async (request, init) => {
            const json = request.headers.get("content-type")?.includes("json") ?? false;
            // A procedure without input sends no body at all (the server reads none as no input).
            const bytes = request.method === "GET" || request.method === "HEAD"
                ? null
                : json
                    ? await request.text()
                    : await request.arrayBuffer();
            const body = bytes instanceof ArrayBuffer && bytes.byteLength === 0 ? null : bytes;
            const response = await send(request.url, {
                ...init,
                method: request.method,
                headers: request.headers,
                body,
                signal: request.signal,
            });
            if (!response.ok)
                throw await readError(response);
            // A page where the API was due (a wrong baseUrl answered by the site's index.html).
            if (response.headers.get("content-type")?.startsWith("text/html"))
                throw new ApiClientError({
                    code: `HTTP_${response.status}`,
                    status: response.status,
                    message: `${request.url} answered a page, not the API: check baseUrl`,
                    traceId: response.headers.get("x-request-id") ?? undefined,
                });
            return response;
        },
        interceptors: [
            async ({ next }) => {
                try {
                    return await next();
                }
                catch (error) {
                    throw toApiClientError(error);
                }
            },
        ],
    });
    return { apiUrl, send, version: () => lastVersion, rpc: createORPCClient(link) };
}
/** A client of /api/v5 on one site, with one credential. */
export function createClient(options) {
    assertCredentialAllowed(options.credential);
    const core = createCore(options, options.credential);
    const { apiUrl, send, rpc } = core;
    const routes = {
        runEvents: (runId, query = {}) => apiUrl(routePath(API_ROUTES.runEvents, { runId }, query)),
        runOutputContent: (runId, path, mode) => apiUrl(routePath(API_ROUTES.runOutputContent, { runId }, { path, mode })),
        projectUpload: (projectId) => apiUrl(routePath(API_ROUTES.projectUpload, { projectId })),
        projectEntryContent: (projectId, entryKey, mode) => apiUrl(routePath(API_ROUTES.projectEntryContent, { projectId, entryKey }, { mode })),
        catalogSnapshot: (locale, shelf) => apiUrl(routePath(API_ROUTES.catalogSnapshot, {}, { locale, shelf })),
    };
    /** A hand-written route's answer, or its refusal as the one error type. */
    async function ok(url, init, authenticate = true) {
        const response = await send(url, init, { authenticate });
        if (!response.ok && response.status !== 304)
            throw await readError(response);
        return response;
    }
    async function* listEvents(runId, { afterSequence = -1, limit, signal } = {}) {
        let after = afterSequence;
        while (true) {
            const page = await rpc.runs.events({ runId, afterSequence: after, ...(limit === undefined ? {} : { limit }) }, signal ? { signal } : {});
            yield page;
            // `nextSequence` is the last stored row read, which may lie past the last envelope: a page
            // with no envelopes is not the end; null is.
            if (page.nextSequence === null || page.nextSequence <= after)
                return;
            after = page.nextSequence;
        }
    }
    return {
        rpc,
        routes,
        streamRunEvents: (runId, streamOptions) => streamRunEvents({ apiUrl, send: (url, init) => send(url, init) }, runId, streamOptions),
        listEvents,
        download: (runId, path, { mode = "download", signal } = {}) => ok(routes.runOutputContent(runId, path, mode), {
            method: "GET",
            ...(signal ? { signal } : {}),
        }),
        projectEntryContent: (projectId, entryKey, { mode = "download", signal } = {}) => ok(routes.projectEntryContent(projectId, entryKey, mode), {
            method: "GET",
            ...(signal ? { signal } : {}),
        }),
        async upload(projectId, file, { fileName, mode = "create", destination, idempotencyKey, signal } = {}) {
            const form = new FormData();
            if (destination)
                form.set("destination", destination);
            // Always named, so a save never replaces a file by default, whatever the server's default.
            form.set("mode", mode);
            const name = fileName ?? file.name ?? "upload";
            form.set("file", file, name);
            const response = await ok(routes.projectUpload(projectId), {
                method: "POST",
                body: form,
                headers: {
                    accept: "application/json",
                    ...(idempotencyKey ? { "idempotency-key": idempotencyKey } : {}),
                },
                ...(signal ? { signal } : {}),
            });
            return (await response.json());
        },
        async snapshot(locale, { ifNoneMatch, shelf, signal } = {}) {
            const response = await ok(routes.catalogSnapshot(locale, shelf), {
                method: "GET",
                headers: {
                    accept: "application/json",
                    ...(ifNoneMatch ? { "if-none-match": ifNoneMatch } : {}),
                },
                ...(signal ? { signal } : {}),
            }, false);
            const etag = response.headers.get("etag");
            if (response.status === 304)
                return { notModified: true, etag };
            return { notModified: false, snapshot: (await response.json()), etag };
        },
        version: core.version,
    };
}
/**
 * Signs a person in to a bearer session (decision D40): `identity.exchange {idToken, delivery:
 * "bearer"}`, sent with no credential, answers the bootstrap and `session: {token: "v5s_…",
 * expiresAt}`. Use the token as `{kind: "bearer", token}`; on a 401 `session_invalid`, drop it
 * and exchange again. The page's origin must be on its site's allowlist.
 */
export async function exchangeBearerSession(options) {
    const { rpc } = createCore(options, null);
    const result = await rpc.identity.exchange({
        idToken: options.idToken,
        delivery: "bearer",
        ...(options.locale ? { locale: options.locale } : {}),
    });
    if (!result.session)
        throw new ApiClientError({
            code: "bearer_session_missing",
            status: 200,
            message: "identity.exchange answered no bearer session",
        });
    return { ...result, session: result.session };
}
//# sourceMappingURL=client.js.map